import Betterbugs from '@betterbugs/web-sdk';
import i18n from '../i18n/config';
import { useAuthStore } from '../stores/authStore';
import { useCadrageStore } from '../stores/cadrageStore';
import { useLayoutStore } from '../stores/layoutStore';

const API_KEY = import.meta.env.VITE_BETTERBUGS_API_KEY;

// Don't initialize if API_KEY is missing or is the default placeholder
const isValidApiKey = API_KEY && API_KEY !== 'your_betterbugs_api_key_here' && API_KEY.length > 10;

type Lang = 'fr' | 'en';

// The widget is rebuilt on language change, so labels are resolved for the
// widget's own language rather than whatever i18n happens to be on.
function buildLabels(lang: Lang) {
  const t = i18n.getFixedT(lang);
  return {
    mainHeading: t('bugReport.widget.mainHeading'),
    emailFieldLabel: t('bugReport.widget.emailFieldLabel'),
    emailFieldPlaceholder: t('bugReport.widget.emailFieldPlaceholder'),
    titleFieldLabel: t('bugReport.widget.titleFieldLabel'),
    titleFieldPlaceholder: t('bugReport.widget.titleFieldPlaceholder'),
    descriptionFieldLabel: t('bugReport.widget.descriptionFieldLabel'),
    descriptionFieldPlaceholder: t('bugReport.widget.descriptionFieldPlaceholder'),
    successMessageHeaderText: t('bugReport.widget.successMessageHeaderText'),
    successMessageSubHeaderText: t('bugReport.widget.successMessageSubHeaderText'),
  };
}

let instance: InstanceType<typeof Betterbugs> | null = null;
let currentEmail: string | undefined;
let currentLang: Lang = 'fr';

function buildConfig(email?: string) {
  return {
    apiKey: API_KEY!,
    showActionButton: false,
    enableAnnotation: false,
    styles: {
      primaryColor: '#db4f0c',
      primaryTextColor: '#fff',
    },
    ...(email ? { email } : {}),
    ...buildLabels(currentLang),
  };
}

type MetaValue = string | number | null;

function collectMetadata(): Record<string, MetaValue> {
  const meta: Record<string, MetaValue> = {
    url: typeof window !== 'undefined' ? window.location.href : '',
    app_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '',
  };

  try {
    const auth = useAuthStore.getState();
    const effectiveUser = auth.impersonatedUser ?? auth.user;
    if (effectiveUser) {
      meta.user_id = effectiveUser.id;
      meta.user_name = effectiveUser.name;
      meta.user_email = effectiveUser.email;
      meta.user_role = effectiveUser.role;
      meta.organization_id = effectiveUser.organization_id ?? '';
      meta.is_impersonating = auth.impersonatedUser != null ? 1 : 0;
    }

    const layout = useLayoutStore.getState();
    meta.sidebar_collapsed = layout.isSidebarCollapsed || layout.isSidebarForced ? 1 : 0;

    const cadrage = useCadrageStore.getState();
    if (cadrage.cadrageData) {
      meta.cadrage_id = cadrage.cadrageData.id;
      meta.cadrage_name = cadrage.cadrageData.name ?? '';
    }
    if (cadrage.currentUseCaseDetail) {
      meta.use_case_id = cadrage.currentUseCaseDetail.id;
      meta.use_case_name = cadrage.currentUseCaseDetail.name ?? '';
    }

    const storesSummary = {
      layout: { isSidebarCollapsed: layout.isSidebarCollapsed, isFullscreen: layout.isFullscreen },
      cadrage: cadrage.cadrageData
        ? { id: cadrage.cadrageData.id, name: cadrage.cadrageData.name }
        : null,
    };
    meta.stores_json = JSON.stringify(storesSummary);
  } catch {
    meta.error = 'metadata_collection_failed';
  }

  return meta;
}

function hideFieldsInWidget(): void {
  requestAnimationFrame(() => {
    setTimeout(() => {
      instance?.setMetadata(collectMetadata());

      const widget = document.querySelector('[class*="betterbugs"]');
      if (!widget) return;
      const fieldsToHide = widget.querySelectorAll(
        'input[type="email"], input[name="email"], input[name="assignee"], input[name="reporter"], select[name="assignee"], select[name="reporter"]'
      );
      fieldsToHide.forEach((el) => {
        const fieldGroup = el.closest('div[class]');
        if (fieldGroup) (fieldGroup as HTMLElement).style.display = 'none';
      });
    }, 150);
  });
}

function createInstance(): InstanceType<typeof Betterbugs> | null {
  try {
    const bb = new Betterbugs(buildConfig(currentEmail));
    bb.on('widgetOpen', hideFieldsInWidget);
    // The SDK sometimes renders the floating action button regardless of
    // showActionButton: false in the config. Call the method explicitly so
    // the button is never visible — the header button is the single entry point.
    bb.hideActionButton?.();
    return bb;
  } catch (error) {
    // Silently catch any initialization errors (e.g., invalid API key, network issues)
    console.warn('BetterBugs initialization failed:', error);
    return null;
  }
}

export function initBetterbugs(): void {
  if (!isValidApiKey) {
    console.info('BetterBugs not initialized: API key not configured or invalid');
    return;
  }
  try {
    instance = createInstance();
  } catch (error) {
    console.warn('BetterBugs initialization error:', error);
  }
}

export function openBetterbugsWidget(): void {
  instance?.openWidget();
}

export function setBetterbugsUser(email: string): void {
  if (!instance) return;
  currentEmail = email;
  instance.setEmail(email);
}

export function setBetterbugsLanguage(lang: string): void {
  if (!isValidApiKey || !instance) return;
  const normalized: Lang = lang.startsWith('en') ? 'en' : 'fr';
  if (normalized === currentLang) return;
  currentLang = normalized;
  try {
    instance.destroy();
    instance = createInstance();
  } catch (error) {
    console.warn('BetterBugs language update error:', error);
  }
}
